import HubspotEmbed from "@/components/forms/hubspot-embed";
import { Box, Typography } from "@mui/material";
import Link from "next/link";
import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslations } from "next-intl";
import useHubspotEmbed from "@/hooks/forms/use-hubspot-embed";
import { NeedMoreInfo } from "@/types/dto/common/need-more-info";
import ContactFormSkeleton from "@/components/forms/contact-form-section/contact-form.skeleton";

interface ContactInfoFormProps {
  formData: NeedMoreInfo["hubSpotFormInfo"];
  showForm?: ((show: boolean) => void) | undefined;
}

const ContactInfoForm: React.FC<ContactInfoFormProps> = ({ formData, showForm }) => {
  const t = useTranslations("COMMON");

  const { dataLayerProps, isLoading, targetId } = useHubspotEmbed({
    formId: formData?.formId || "264d873e-f8e2-47bb-ad9e-c9093fadfe8b",
    portalId: formData?.portalId || "2641890",
    formName: formData?.formName || "Contact Information"
  });

  if (isLoading) {
    return (
      <Box sx={{ padding: "2.5rem" }}>
        <ContactFormSkeleton />;
      </Box>
    );
  }

  return (
    <Box
      id="contact-info-form"
      data-testid="contact-info-form"
      component="section"
      sx={{
        borderRadius: "1.25rem",
        backgroundColor: "background.default",
        padding: "2.5rem",
        boxShadow: 6,
        justifyContent: "space-between"
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography
          data-testid="contact-info-form-title"
          variant="h6"
          component="h3"
          sx={{
            color: "text.primary",
            paddingBottom: "2.4rem",
            fontSize: "20px !important",
            fontWeight: "600 !important"
          }}
        >
          {formData?.formTitle || "Contact Information"}
        </Typography>
        {typeof showForm === "function" && (
          <CloseIcon sx={{ cursor: "pointer" }} onClick={() => showForm(false)} />
        )}
      </Box>

      <Box data-testid="contact-info-hubspot-form-container">
        <HubspotEmbed
          formId={formData?.formId || "264d873e-f8e2-47bb-ad9e-c9093fadfe8b"}
          dataLayerProps={dataLayerProps}
          targetId={targetId}
          data-testid="contact-info-hubspot-embed"
        />
      </Box>
      <Typography
        data-testid="contact-info-form-disclaimer"
        variant="subtitle1"
        component="p"
        sx={{
          textAlign: "center",
          color: "text.primary",
          paddingTop: "1.2rem",
          fontSize: "1.4rem",
          fontWeight: "500"
        }}
      >
        {t("Disclaimer")}
        <Link
          data-testid="contact-info-form-terms-of-use-link"
          href="/about-collette/terms-and-conditions"
          style={{ color: "var(--theme-palette-text-primary)", textDecoration: "underline" }}
        >
          {t("Terms of Use")}
        </Link>
        and
        <Link
          href="/about-collette/privacy-policy"
          data-testid="contact-info-privacy-policy-link"
          style={{ color: "var(--theme-palette-text-primary)", textDecoration: "underline" }}
        >
          {t("Privacy Policy")}
        </Link>
        .
      </Typography>
    </Box>
  );
};

export default ContactInfoForm;
